import { IconClock, IconCreditCard, IconInfoCircle, IconMapPin, IconUsers } from '@tabler/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Group, Stack, Text } from '@mantine/core'
import { BooleanTextMessage } from '@/components/Elements'
import { OnetimeRanges } from '@/features/bookings/components/Booking/BookingDetails/BookingTableDetails/OnetimeRanges'
import { BOOKING_TYPE } from '@/features/bookings/consts/booking'
import { bookingHelper } from '@/features/bookings/helpers'
import { DateUtils } from '@/utils'
import { BookingWizardFormBanner } from '../../components/Booking/BookingContainer/BookingWizardForm'
import useStyles from './CheckoutForm.styles'

type ItemType = {
  icon: any
  label: string
  value: React.ReactNode
  hidden?: boolean
}

export const BookingCheckoutDetails = () => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const { booking } = useSelector((state: any) => state.booking.checkout)

  const timePeriod = bookingHelper.bookingModel.getTimePeriod(booking)

  const {
    zip,
    type,
    date_start,
    date_end,
    living_in,
    hours_per_week,
    days_per_week,
    booking_children,
    extra_activities,
    booking_animation_children_0_2,
    booking_animation_children_3_4,
    booking_animation_children_4,
    entertainment_kit,
    has_special_needs,
    special_needs,
    care_expectations,
    address,
  } = booking

  const items: ItemType[] = [
    {
      icon: <IconInfoCircle size={16} />,
      label: t('type'),
      value: bookingHelper.type.getType(type)?.title,
    },
    {
      icon: <IconMapPin size={16} />,
      label: t('location'),
      value: address + ' ' + zip,
    },
    {
      icon: <IconClock size={16} />,
      label: t('book.date_time'),
      value:
        (type == BOOKING_TYPE.REGULAR || type == BOOKING_TYPE.PERMANENT_NANNY
          ? DateUtils.formatDate(date_start) + ' - ' + DateUtils.formatDate(date_end)
          : DateUtils.formatDate(date_start)) +
        ' ' +
        timePeriod?.time_start +
        ' - ' +
        timePeriod?.time_end,
    },
    {
      icon: <IconClock size={16} />,
      label: t('hours_per_week'),
      value: <span className={'whitespace-nowrap'}>{hours_per_week}</span>,
      hidden:
        type != BOOKING_TYPE.PERMANENT_NANNY ||
        (type == BOOKING_TYPE.PERMANENT_NANNY && (living_in || (!living_in && !hours_per_week))),
    },
    {
      icon: <IconClock size={16} />,
      label: t('days_per_week'),
      value: <span className={'whitespace-nowrap'}>{days_per_week}</span>,
      hidden:
        type != BOOKING_TYPE.PERMANENT_NANNY ||
        (type == BOOKING_TYPE.PERMANENT_NANNY && !living_in),
    },
    {
      icon: <IconClock size={16} />,
      label: t('added_dates'),
      value: <OnetimeRanges data={booking.timetable.onetime_ranges} />,
      hidden:
        !booking ||
        (booking.type !== BOOKING_TYPE.ONETIME && booking.type !== BOOKING_TYPE.ANIMATION) ||
        !booking.timetable.onetime_ranges ||
        booking.timetable.onetime_ranges.length == 0,
    } /*
    {
      label: t('comment'),
      value: comments ? <span className={'whitespace-pre-wrap'}>{comments}</span> : '-',
    },*/,
    {
      icon: <IconUsers size={16} />,
      label: t('children'),
      value: (
        <>
          {type !== BOOKING_TYPE.ANIMATION &&
            booking_children.map((item: any, i: any) => (
              <React.Fragment key={i}>
                <Text weight={600}>{item?.name}</Text>
                <Text>{item?.birthday ? DateUtils.formatDate(item?.birthday) : null}</Text>
              </React.Fragment>
            ))}
          {type === BOOKING_TYPE.ANIMATION &&
            booking_animation_children_0_2?.map((item: any, i: any) => (
              <React.Fragment key={i}>
                <Text weight={600}>{item?.name}</Text>
                <Text>{item?.kid_age ? item?.kid_age + ' ' + t('months') : null}</Text>
              </React.Fragment>
            ))}
          {type === BOOKING_TYPE.ANIMATION &&
            booking_animation_children_3_4?.map((item: any, i: any) => (
              <React.Fragment key={i}>
                <Text weight={600}>{item?.name}</Text>
                <Text>{item?.kid_age ? item?.kid_age + ' ' + t('years') : null}</Text>
              </React.Fragment>
            ))}
          {type === BOOKING_TYPE.ANIMATION &&
            booking_animation_children_4?.map((item: any, i: any) => (
              <React.Fragment key={i}>
                <Text weight={600}>{item?.name}</Text>
                <Text>{item?.kid_age ? item?.kid_age + ' ' + t('years') : null}</Text>
              </React.Fragment>
            ))}
        </>
      ),
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('kid_has_special_needs'),
      value: <BooleanTextMessage value={has_special_needs} />,
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('special_needs'),
      value: special_needs ? <span className={'whitespace-pre-wrap'}>{special_needs}</span> : '-',
      hidden: !has_special_needs,
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('price.entertainment_kit'),
      value: <BooleanTextMessage value={entertainment_kit} />,
      hidden: type != BOOKING_TYPE.ANIMATION,
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('facial_painting'),
      value: <BooleanTextMessage value={extra_activities?.facial_painting} />,
      hidden: type != BOOKING_TYPE.ANIMATION || !extra_activities?.facial_painting,
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('balloon_modeling'),
      value: <BooleanTextMessage value={extra_activities?.balloon_modeling} />,
      hidden: type != BOOKING_TYPE.ANIMATION || !extra_activities?.balloon_modeling,
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('extra_activities_hours'),
      value: extra_activities?.hours,
      hidden:
        type != BOOKING_TYPE.ANIMATION ||
        (!extra_activities?.facial_painting && !extra_activities?.balloon_modeling),
    },
    {
      icon: <IconInfoCircle size={16} />,
      label: t('expectations_for_the_care'),
      value: care_expectations ? (
        <span className={'whitespace-pre-wrap'}>{care_expectations}</span>
      ) : (
        '-'
      ),
    },
  ]

  const renderGroup = (icon: any, label: any, value: any) => (
    <>
      <div className={classes.detailsTitle}>
        <div className={classes.icon}>{icon}</div>
        <span>{label}</span>
      </div>
      <div className={classes.detailsContent}>
        <span>{value ? value : '-'}</span>
      </div>
    </>
  )

  return (
    booking && (
      <>
        <Stack>
          <BookingWizardFormBanner
            icon={<IconCreditCard size={14} />}
            text={t('book.service_payment_disclaimer')}
            color={'#fbf3ff'}
          />
          <div className={classes.details}>
            {items.map(
              ({ label, value, icon, hidden, ...item }, index) =>
                !hidden && (
                  <React.Fragment key={index}>{renderGroup(icon, label, value)}</React.Fragment>
                )
            )}
            <hr />
            <Group className="w-100" position="apart">
              <div className="flex gap-1 font-bold">
                <span>{t('book.total_payment')}</span>
              </div>
              <div className="flex gap-1 font-bold">
                <span>€{((booking.total_amount || 0) / 100).toFixed(2)}</span>
              </div>
            </Group>
            <div className="flex gap-1" style={{ color: 'grey' }}>
              <span>{t('book.payment.charge')}</span>
            </div>
          </div>
        </Stack>
      </>
    )
  )
}
